import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { TrendingUp, ArrowRight, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import { isWebAuthnSupported, loginWithPasskey, hasPasskey } from "../lib/webauthn";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Send stored device_token if we have one — server may skip TOTP prompt
      const deviceToken = localStorage.getItem("device_token") || null;
      const payload = { email, password };
      if (needs2fa && !useRecovery && totpCode) payload.totp_code = totpCode;
      if (needs2fa && useRecovery && recoveryCode) payload.recovery_code = recoveryCode;
      if (deviceToken) payload.device_token = deviceToken;
      if (trustDevice) payload.trust_device = true;
      const { data } = await api.post("/auth/login", payload);
      localStorage.setItem("token", data.token);
      if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
      if (data.device_token) localStorage.setItem("device_token", data.device_token);
      setUser?.(data.user);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      // If a device_token was rejected (e.g. expired), clear it and retry manually
      if (String(msg).toLowerCase().includes("totp") || String(msg).toLowerCase().includes("2fa")) {
        setNeeds2fa(true);
        toast.info("Enter your 2FA code");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const faceId = async () => {
    if (!email) return toast.error("Enter your email first");
    if (!isWebAuthnSupported()) return toast.error("This device doesn't support passkeys");
    try {
      const ok = await hasPasskey(email);
      if (!ok) return toast.error("No passkey found for this email. Sign in once, then enable it in Settings.");
      const data = await loginWithPasskey(email);
      localStorage.setItem("token", data.token);
      setUser?.(data.user);
      toast.success("Signed in with Face ID");
      navigate("/");
    } catch (err) {
      toast.error(err?.message || "Face ID sign-in cancelled");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative bg-card border-r border-border overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-2">
            <img src="/brand/aanar-mark-reversed.svg" alt="Aanar" className="w-10 h-10 dark:block hidden" />
            <img src="/brand/aanar-mark.svg" alt="Aanar" className="w-10 h-10 dark:hidden block" />
            <div className="font-display font-bold text-xl">Aanar</div>
          </div>
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--ai))] mb-3">The classic trader's edge</div>
            <h1 className="font-display text-5xl font-bold leading-tight mb-4">
              Trade like the wizards.<br/>Learn like a scholar.
            </h1>
            <p className="text-muted-foreground text-base">
              A paper trading dashboard for stocks & crypto — with AI insights grounded in classic trading books.
            </p>
            <div className="mt-8 flex gap-6 text-sm">
              <div><div className="font-mono-tab text-2xl">$100k</div><div className="text-muted-foreground text-xs">starting cash</div></div>
              <div><div className="font-mono-tab text-2xl">24+</div><div className="text-muted-foreground text-xs">live assets</div></div>
              <div><div className="font-mono-tab text-2xl">1</div><div className="text-muted-foreground text-xs">AI mentor</div></div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground italic">Markets are never wrong; opinions often are. — Jesse Livermore</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <h2 className="font-display text-3xl font-bold">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Continue building your paper portfolio.</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     required autoComplete="email" data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     required autoComplete="current-password" data-testid="login-password" />
              <div className="text-right">
                <Link to="/forgot-password" data-testid="link-forgot"
                      className="text-xs text-muted-foreground hover:text-foreground">
                  Forgot password?
                </Link>
              </div>
            </div>
            {needs2fa && (
              <div className="space-y-1.5" data-testid="login-2fa-section">
                {!useRecovery ? (
                  <>
                    <Label htmlFor="totp">Authenticator code</Label>
                    <Input id="totp" inputMode="numeric" pattern="\d{6}" maxLength={8}
                           placeholder="123456" value={totpCode}
                           onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                           autoComplete="one-time-code" data-testid="login-totp" required />
                    <label className="flex items-center gap-2 mt-2 text-xs text-muted-foreground cursor-pointer" data-testid="trust-device-label">
                      <input
                        type="checkbox"
                        checked={trustDevice}
                        onChange={(e) => setTrustDevice(e.target.checked)}
                        data-testid="trust-device-checkbox"
                        className="rounded"
                      />
                      Trust this device for 30 days (skip 2FA on this browser)
                    </label>
                    <button type="button" onClick={() => setUseRecovery(true)}
                            data-testid="use-recovery-link"
                            className="text-xs text-muted-foreground hover:text-foreground">
                      Use a recovery code instead
                    </button>
                  </>
                ) : (
                  <>
                    <Label htmlFor="recovery">Recovery code</Label>
                    <Input id="recovery" placeholder="ABCDE-12345" value={recoveryCode}
                           onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                           data-testid="login-recovery" required />
                    <button type="button" onClick={() => setUseRecovery(false)}
                            className="text-xs text-muted-foreground hover:text-foreground">
                      Back to authenticator code
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="login-submit">
            {loading ? "Signing in..." : "Sign in"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          {isWebAuthnSupported() && (
            <Button type="button" variant="outline" onClick={faceId}
                    className="w-full rounded-full h-11" data-testid="login-faceid">
              <Fingerprint className="w-4 h-4 mr-2" /> Sign in with Face ID
            </Button>
          )}
          <p className="text-sm text-muted-foreground text-center">
            No account?{" "}
            <Link to="/register" className="text-foreground font-medium hover:underline" data-testid="link-register">
              Create one
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
